import NumberField from '@components/Customization/NumberField';
import ToggleRow from '@components/Customization/ToggleRow';
import { whole } from '@lib/customization/fieldDraft';
import { useLocalization } from '@lib/localization';
import { Box, Chip, FormControl, FormHelperText, FormLabel, Stack, TextField } from '@mui/material';
import { barcodeFormats, type PoiField } from '@store/api/poiTypeEndpoints';

interface Props {
  draft: PoiField;
  patch: (changes: Partial<PoiField>) => void;
  /** Rendered where a choice field's options belong, between the barcode and range blocks. */
  choices?: React.ReactNode;
  /** Shown but not editable, for a reader who may not change the type. */
  disabled?: boolean;
}

/**
 * The settings that depend on a field's input type: placeholder, default, text and number
 * options, barcode formats, range bounds, a toggle's starting state.
 *
 * Shared by the field drawer and the designer's inspector, so a setting means the same in
 * both. The caller wraps it in its own stack; this renders the controls as siblings.
 */
const FieldSettings: React.FC<Props> = ({ draft, patch, choices, disabled }) => {
  const { t } = useLocalization();

  return (
    <>
      {['Text', 'Number', 'Barcode'].includes(draft.type) && (
        <TextField
          disabled={disabled}
          size="small"
          label={t('Customization_Placeholder')}
          value={draft.placeholder ?? ''}
          onChange={(event) => patch({ placeholder: event.target.value })}
        />
      )}

      {/* The three types that gained a default when the per-type keys collapsed into
        `value`. Range and Toggle set theirs in their own blocks below, and a choice
        field marks it with the star on an option. */}
      {draft.type === 'Number' && (
        <NumberField
          disabled={disabled}
          fullWidth
          size="small"
          label={t('Common_Default')}
          allowDecimal={Boolean(draft.decimal)}
          value={typeof draft.value === 'number' ? draft.value : undefined}
          onChange={(value) => patch({ value })}
        />
      )}

      {(draft.type === 'Text' || draft.type === 'Barcode') && (
        <TextField
          disabled={disabled}
          fullWidth
          size="small"
          label={t('Common_Default')}
          multiline={draft.type === 'Text' && Boolean(draft.multiline)}
          value={typeof draft.value === 'string' ? draft.value : ''}
          onChange={(event) => patch({ value: event.target.value || undefined })}
        />
      )}

      {draft.type === 'Text' && (
        <>
          <ToggleRow
            disabled={disabled}
            label={t('Customization_Multiline')}
            description={t('Customization_MultilineHelp')}
            checked={Boolean(draft.multiline)}
            onChange={(checked) => patch({ multiline: checked })}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              disabled={disabled}
              size="small" fullWidth label={t('Customization_ValidationPattern')}
              value={draft.pattern ?? ''} onChange={(event) => patch({ pattern: event.target.value })}
            />
            <NumberField
              disabled={disabled}
              size="small" fullWidth label={t('Customization_CharLimit')}
              allowDecimal={false}
              allowNegative={false}
              value={draft.charLimit}
              onChange={(charLimit) => patch({ charLimit })}
            />
          </Stack>
        </>
      )}

      {(draft.type === 'Number' || draft.type === 'Range') && (
        <ToggleRow
          disabled={disabled}
          label={t('Customization_AllowDecimals')}
          description={draft.type === 'Range'
            ? t('Customization_RangeDecimalsHelp')
            : t('Customization_AllowDecimalsHelp')}
          checked={Boolean(draft.decimal)}
          onChange={(checked) => patch({ decimal: checked, ...(checked ? {} : whole(draft)) })}
        />
      )}

      {draft.type === 'Barcode' && (
        <>
          {/* A labelled control rather than a loose heading, so it reads as one field
            alongside the inputs above and below it. */}
          <FormControl fullWidth>
            <FormLabel sx={{ typography: 'caption', color: 'text.secondary', mb: 1 }}>
              {t('Customization_AcceptedFormats')}
            </FormLabel>
            <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
              {barcodeFormats.map((format) => {
                const selected = (draft.formats ?? []).includes(format);
                return (
                  <Chip
                    disabled={disabled}
                    key={format}
                    size="small"
                    label={format}
                    color={selected ? 'primary' : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    onClick={() => patch({
                      formats: selected
                        ? (draft.formats ?? []).filter((current) => current !== format)
                        : [...(draft.formats ?? []), format],
                    })}
                  />
                );
              })}
            </Stack>
          </FormControl>
          <TextField
            disabled={disabled}
            size="small" label={t('Customization_ValidationPattern')}
            value={draft.pattern ?? ''} onChange={(event) => patch({ pattern: event.target.value })}
          />
        </>
      )}

      {choices}

      {draft.type === 'Range' && (
        <Box>
          <Stack direction="row" spacing={2}>
            <NumberField
              disabled={disabled}
              size="small" fullWidth label={t('Customization_Min')}
              allowDecimal={Boolean(draft.decimal)}
              value={draft.min} onChange={(min) => patch({ min })}
            />
            <NumberField
              disabled={disabled}
              size="small" fullWidth label={t('Customization_Max')}
              allowDecimal={Boolean(draft.decimal)}
              value={draft.max} onChange={(max) => patch({ max })}
            />
            {/* An empty default stays empty — the device starts the slider at Min
              without being told, so writing Min in here would only freeze a bound
              the author may still move. The placeholder shows where it starts. */}
            <NumberField
              disabled={disabled}
              size="small" fullWidth label={t('Common_Default')}
              allowDecimal={Boolean(draft.decimal)}
              placeholder={draft.min === undefined ? undefined : String(draft.min)}
              slotProps={{ inputLabel: { shrink: true } }}
              value={typeof draft.value === 'number' ? draft.value : undefined}
              onChange={(value) => patch({ value })}
            />
          </Stack>
          <FormHelperText sx={{ mx: 0, mt: 1 }}>{t('Customization_RangeDefaultHelp')}</FormHelperText>
        </Box>
      )}

      {draft.type === 'Toggle' && (
        <ToggleRow
          disabled={disabled}
          label={t('Customization_DefaultState')}
          description={t('Customization_DefaultStateHelp')}
          checked={Boolean(draft.value)}
          onChange={(checked) => patch({ value: checked })}
        />
      )}
    </>
  );
};

export default FieldSettings;
